/**
 * The kit's smallest parts: buttons, the spinner, badges, the tooltip, key
 * caps, the progress bar, and the motion helpers the rest of the kit shares.
 * Everything here is re-exported from `Ui.tsx`; screens import from there.
 */
import type { ButtonHTMLAttributes, CSSProperties, ReactElement, ReactNode } from 'react'
import { cloneElement, forwardRef, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import styles from './Primitives.module.css'

/** Joins class names, skipping empty ones. */
export function cx(...names: Array<string | false | null | undefined>): string {
  return names.filter(Boolean).join(' ')
}

/* ------------------------------------------------------------------------ */
/* motion                                                                   */
/* ------------------------------------------------------------------------ */

const REDUCED_QUERY = '(prefers-reduced-motion: reduce)'

/** True when the operator's system asks for less motion; follows changes live. */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(REDUCED_QUERY).matches)
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const query = window.matchMedia(REDUCED_QUERY)
    const update = (): void => setReduced(query.matches)
    update()
    query.addEventListener('change', update)
    return () => query.removeEventListener('change', update)
  }, [])
  return reduced
}

/** Same as the hook, for code outside render (timers, event handlers). */
export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(REDUCED_QUERY).matches
}

const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3)

/**
 * Animates a number towards `target` (600 ms ease-out): from 0 on mount, from
 * the value on screen when the target changes. Returns the target itself
 * under reduced motion or when `enabled` is false.
 */
export function useCountUp(target: number | null, { duration = 600, enabled = true }: { duration?: number; enabled?: boolean } = {}): number | null {
  const reduced = usePrefersReducedMotion()
  const [shown, setShown] = useState<number | null>(() => (target === null ? null : enabled && !reduced ? 0 : target))
  const shownRef = useRef<number | null>(shown)
  shownRef.current = shown
  useEffect(() => {
    if (target === null || !Number.isFinite(target)) {
      setShown(target)
      return
    }
    const from = shownRef.current ?? 0
    if (!enabled || reduced || from === target) {
      setShown(target)
      return
    }
    let frame = 0
    const start = performance.now()
    const step = (now: number): void => {
      const t = Math.min(1, (now - start) / duration)
      setShown(t >= 1 ? target : from + (target - from) * easeOutCubic(t))
      if (t < 1) frame = requestAnimationFrame(step)
    }
    frame = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frame)
  }, [target, duration, enabled, reduced])
  return shown
}

/* ------------------------------------------------------------------------ */
/* spinner                                                                  */
/* ------------------------------------------------------------------------ */

/** A small rotating ring in the current text colour; `label` makes it announce itself. */
export function Spinner({ size = 14, label, className }: { size?: number; label?: string; className?: string }): JSX.Element {
  return (
    <svg
      className={cx(styles.spinner, className)}
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      role={label ? 'status' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeOpacity="0.22" strokeWidth="2" />
      <path d="M14.5 8A6.5 6.5 0 0 0 8 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}

/* ------------------------------------------------------------------------ */
/* buttons                                                                  */
/* ------------------------------------------------------------------------ */

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'quiet' | 'danger' | 'danger-solid'
export type ButtonSize = 'sm' | 'md' | 'lg'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** primary (accent, one per view) · secondary = outline (default) · ghost = quiet · danger (outlined red) · danger-solid (filled red, for a destructive confirm). */
  variant?: ButtonVariant
  /** sm 32 · md 36 · lg 40 px high. */
  size?: ButtonSize
  /** Shows a spinner over the label (the width stays) and disables the button. */
  loading?: boolean
  /** Leading icon, e.g. `<Plus size={16} />`. */
  icon?: ReactNode
  /** Trailing icon. */
  iconRight?: ReactNode
  /** Stretches to the container's width. */
  block?: boolean
}

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: styles.primary,
  secondary: styles.secondary,
  outline: styles.secondary,
  ghost: styles.ghost,
  quiet: styles.ghost,
  danger: styles.danger,
  'danger-solid': styles.dangerSolid,
}

const SIZE_CLASS: Record<ButtonSize, string> = { sm: styles.sm, md: styles.md, lg: styles.lg }

/** The push button: variants, three sizes, optional icons and a loading state; presses scale to .98. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'outline', size = 'md', loading = false, icon, iconRight, block = false, className, children, disabled, ...rest },
  ref,
): JSX.Element {
  const iconOnly = children === undefined || children === null || children === false
  return (
    <button
      ref={ref}
      type="button"
      className={cx(styles.button, VARIANT_CLASS[variant], SIZE_CLASS[size], iconOnly && icon ? styles.iconOnly : '', block ? styles.block : '', loading ? styles.loading : '', className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      <span className={styles.content}>
        {icon ? <span className={styles.icon}>{icon}</span> : null}
        {iconOnly ? null : <span className={styles.text}>{children}</span>}
        {iconRight ? <span className={styles.icon}>{iconRight}</span> : null}
      </span>
      {loading ? (
        <span className={styles.spinnerSlot} aria-hidden="true">
          <Spinner size={size === 'sm' ? 13 : 15} />
        </span>
      ) : null}
    </button>
  )
})

export interface IconButtonProps extends Omit<ButtonProps, 'icon' | 'iconRight' | 'children' | 'block'> {
  /** The icon, e.g. `<X size={16} />`. */
  icon: ReactNode
  /** Accessible name; also the tooltip text. */
  label: string
  /** Show `label` as a tooltip on hover/focus (default true). */
  tooltip?: boolean
  tooltipSide?: TooltipSide
}

/** A square icon-only button (ghost by default) with its label as aria-label and tooltip. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, tooltip = true, tooltipSide = 'top', variant = 'ghost', size = 'md', ...rest },
  ref,
): JSX.Element {
  const button = <Button ref={ref} variant={variant} size={size} icon={icon} aria-label={label} {...rest} />
  return tooltip ? (
    <Tooltip content={label} side={tooltipSide}>
      {button}
    </Tooltip>
  ) : (
    button
  )
})

/* ------------------------------------------------------------------------ */
/* badges                                                                   */
/* ------------------------------------------------------------------------ */

export type Tone = 'neutral' | 'accent' | 'warning' | 'down' | 'info'

const TONE_CLASS: Record<Tone, string> = {
  neutral: styles.toneNeutral,
  accent: styles.toneAccent,
  warning: styles.toneWarning,
  down: styles.toneDown,
  info: styles.toneInfo,
}

/** The tone class of the kit (wash fill, edge, text colour) for custom chips. */
export function toneClass(tone: Tone): string {
  return TONE_CLASS[tone]
}

export interface BadgeProps {
  tone?: Tone
  /** Leading status dot (default true). */
  dot?: boolean
  /** The dot breathes slowly (2.4 s) — for live, running, sending states. */
  pulse?: boolean
  /** Figures in the mono face. */
  mono?: boolean
  /** Icon in place of the dot. */
  icon?: ReactNode
  size?: 'sm' | 'md'
  title?: string
  className?: string
  children: ReactNode
}

/** A pill with a dot and text in one of five tones: neutral, accent, warning, down, info. */
export function Badge({ tone = 'neutral', dot = true, pulse = false, mono = false, icon, size = 'md', title, className, children }: BadgeProps): JSX.Element {
  return (
    <span className={cx(styles.badge, TONE_CLASS[tone], size === 'sm' ? styles.badgeSm : '', mono ? styles.mono : '', className)} title={title}>
      {icon ? <span className={styles.badgeIcon}>{icon}</span> : dot ? <span className={cx(styles.dot, pulse ? styles.pulse : '')} aria-hidden="true" /> : null}
      {children}
    </span>
  )
}

/** A small count pill (sidebar queues, tab counters); warning tone for a queue that waits. */
export function Count({ children, tone = 'neutral', className }: { children: ReactNode; tone?: Tone; className?: string }): JSX.Element {
  return <span className={cx(styles.count, TONE_CLASS[tone], className)}>{children}</span>
}

/** A dot on its own (health, presence); `pulse` for live. */
export function StatusDot({ tone = 'accent', pulse = false, label }: { tone?: Tone; pulse?: boolean; label?: string }): JSX.Element {
  return <span className={cx(styles.statusDot, TONE_CLASS[tone], pulse ? styles.pulse : '')} role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true} />
}

/** A keyboard key cap, e.g. `<Kbd>⌘K</Kbd>`. */
export function Kbd({ children, className }: { children: ReactNode; className?: string }): JSX.Element {
  return <kbd className={cx(styles.kbd, className)}>{children}</kbd>
}

/** A thin bar for a share of a budget or limit; `value` of `max`. */
export function Progress({ value, max = 100, tone = 'accent', label, className }: { value: number; max?: number; tone?: Tone; label?: string; className?: string }): JSX.Element {
  const share = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0
  return (
    <span
      className={cx(styles.progress, TONE_CLASS[tone], className)}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={value}
      aria-label={label}
    >
      <span className={styles.progressFill} style={{ '--share': share } as CSSProperties} />
    </span>
  )
}

/* ------------------------------------------------------------------------ */
/* tooltip                                                                  */
/* ------------------------------------------------------------------------ */

export type TooltipSide = 'top' | 'bottom' | 'left' | 'right'

interface TooltipProps {
  /** What the tooltip says; nothing renders when empty. */
  content: ReactNode
  /** One element to describe (a button, an icon, a chip). */
  children: ReactElement
  side?: TooltipSide
  /** Hover delay in ms (focus shows at once). */
  delay?: number
  disabled?: boolean
}

/**
 * A small label on hover or keyboard focus. It is portalled into the open
 * dialog (so it shows above a modal) or the body, positioned against the
 * viewport and flipped when it would leave it; Escape hides it.
 */
export function Tooltip({ content, children, side = 'top', delay = 350, disabled = false }: TooltipProps): JSX.Element {
  const anchorRef = useRef<HTMLSpanElement>(null)
  const tipRef = useRef<HTMLDivElement>(null)
  const timer = useRef<number | undefined>(undefined)
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<{ x: number; y: number; side: TooltipSide } | null>(null)
  const id = useId()

  const show = useCallback(
    (wait: number) => {
      window.clearTimeout(timer.current)
      timer.current = window.setTimeout(() => setOpen(true), wait)
    },
    [],
  )
  const hide = useCallback(() => {
    window.clearTimeout(timer.current)
    setOpen(false)
    setPosition(null)
  }, [])

  useEffect(() => () => window.clearTimeout(timer.current), [])

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') hide()
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('scroll', hide, true)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', hide, true)
    }
  }, [open, hide])

  useLayoutEffect(() => {
    if (!open) return
    const anchor = anchorRef.current?.firstElementChild ?? anchorRef.current
    const tip = tipRef.current
    if (!anchor || !tip) return
    const a = anchor.getBoundingClientRect()
    const w = tip.offsetWidth
    const h = tip.offsetHeight
    const gap = 8
    const margin = 8
    const vw = window.innerWidth
    const vh = window.innerHeight
    let chosen = side
    if (chosen === 'top' && a.top - h - gap < margin) chosen = 'bottom'
    else if (chosen === 'bottom' && a.bottom + h + gap > vh - margin) chosen = 'top'
    else if (chosen === 'left' && a.left - w - gap < margin) chosen = 'right'
    else if (chosen === 'right' && a.right + w + gap > vw - margin) chosen = 'left'
    let x: number
    let y: number
    if (chosen === 'top' || chosen === 'bottom') {
      x = a.left + a.width / 2 - w / 2
      y = chosen === 'top' ? a.top - h - gap : a.bottom + gap
    } else {
      x = chosen === 'left' ? a.left - w - gap : a.right + gap
      y = a.top + a.height / 2 - h / 2
    }
    x = Math.max(margin, Math.min(vw - w - margin, x))
    y = Math.max(margin, Math.min(vh - h - margin, y))
    setPosition({ x, y, side: chosen })
  }, [open, side, content])

  const empty = content === null || content === undefined || content === '' || content === false
  const visible = open && !disabled && !empty
  const host = visible && typeof document !== 'undefined' ? (anchorRef.current?.closest('dialog[open]') as HTMLElement | null) ?? document.body : null

  return (
    <span
      ref={anchorRef}
      className={styles.tipAnchor}
      onMouseEnter={() => show(delay)}
      onMouseLeave={hide}
      onFocus={(event) => {
        if ((event.target as HTMLElement).matches?.(':focus-visible')) show(0)
      }}
      onBlur={hide}
      onPointerDown={hide}
    >
      {visible ? cloneElement(children, { 'aria-describedby': id } as Record<string, unknown>) : children}
      {visible && host
        ? createPortal(
            <div
              ref={tipRef}
              id={id}
              role="tooltip"
              className={styles.tooltip}
              data-side={position?.side ?? side}
              data-ready={position ? '' : undefined}
              style={position ? { left: position.x, top: position.y } : { left: -9999, top: -9999 }}
            >
              {content}
            </div>,
            host,
          )
        : null}
    </span>
  )
}
